<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { Button } from "@/components/ui/button";

interface Props {
    text?: string;
    valueHelpTexts?: Record<string, string>;
    selectedValue?: string | number | null;
    iconClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
    text: "",
    valueHelpTexts: () => ({}),
    selectedValue: null,
    iconClass: "w-4 h-4 text-gray-500"
});

const isVisible = ref(false);
const isPositioned = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipStyle = ref<Record<string, string>>({
    position: "fixed",
    top: "-9999px",
    left: "-9999px",
    maxWidth: "min(24rem, calc(100vw - 16px))"
});
const tooltipId = `help-popover-${Math.random().toString(36).slice(2, 11)}`;

const resolvedText = computed(() => {
    if (props.selectedValue === null || props.selectedValue === undefined) {
        return props.text;
    }

    const mapped = props.valueHelpTexts[String(props.selectedValue)];
    return mapped && mapped.length > 0 ? mapped : props.text;
});

const hasText = computed(() => resolvedText.value.trim().length > 0);

function removePositionListeners(): void {
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", updatePosition, true);
}

function addPositionListeners(): void {
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
}

function updatePosition(): void {
    if (!isVisible.value || !triggerRef.value || !tooltipRef.value) {
        return;
    }

    const triggerRect = triggerRef.value.getBoundingClientRect();
    const tooltipRect = tooltipRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const gap = 6;

    let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));

    const canRenderBottom = triggerRect.bottom + gap + tooltipRect.height + margin <= viewportHeight;
    const top = canRenderBottom
        ? triggerRect.bottom + gap
        : Math.max(margin, triggerRect.top - tooltipRect.height - gap);

    tooltipStyle.value = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        maxWidth: "min(24rem, calc(100vw - 16px))"
    };
}

async function showTooltip(): Promise<void> {
    if (!hasText.value) {
        return;
    }

    isVisible.value = true;
    isPositioned.value = false;
    await nextTick();
    updatePosition();
    isPositioned.value = true;
    addPositionListeners();
}

function hideTooltip(): void {
    isVisible.value = false;
    isPositioned.value = false;
    removePositionListeners();
}

function toggleTooltip(): void {
    if (isVisible.value) {
        hideTooltip();
        return;
    }

    void showTooltip();
}

watch(resolvedText, async () => {
    if (!isVisible.value) {
        return;
    }

    await nextTick();
    updatePosition();
});

onBeforeUnmount(() => {
    removePositionListeners();
});
</script>

<template>
    <span v-if="hasText" class="inline-flex align-middle">
        <button
            ref="triggerRef"
            type="button"
            size="icon"
            class="inline-flex items-center cursor-help rounded-sm text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vde-blau2 focus-visible:ring-offset-2"
            :aria-describedby="isVisible ? tooltipId : undefined"
            aria-label="Help"
            @mouseenter="showTooltip"
            @mouseleave="hideTooltip"
            @blur="hideTooltip"
            @click="toggleTooltip"
            @keydown.escape="hideTooltip"
        >
            <svg :class="props.iconClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
        </button>
    </span>

    <Teleport to="body">
        <span
            v-if="hasText"
            v-show="isVisible && isPositioned"
            :id="tooltipId"
            ref="tooltipRef"
            :style="tooltipStyle"
            class="pointer-events-none z-[90] rounded bg-black px-2 py-1 text-xs text-white shadow-lg"
            role="tooltip"
            v-html="resolvedText"
        />
    </Teleport>
</template>
